import axios from "axios";
import { API_BASE_URL } from "../config/apiConfig";

let isRefreshing = false;
let refreshSubscribers = [];
let inFlightRequests = new Map();
let queuedRequests = [];

export const apiRequest = async (
  method = "get",
  url = "",
  payload = { body: null, queries: null },
  contentType = "application/json",
  uploadProgress = () => {},
  customHeader = {}
) => {
  let access_token = localStorage.getItem("auth_token");
  let refresh_token = localStorage.getItem("refreshToken");
  let userId = localStorage.getItem("userId");

  const requestKey = `${method.toLowerCase()}-${url}-${JSON.stringify(
    payload
  )}`;
  if (inFlightRequests.has(requestKey)) {
    return inFlightRequests.get(requestKey);
  }

  const instance = axios.create({
    baseURL: API_BASE_URL,
    headers: {
      "Content-Type": contentType,
      Accept: contentType,
      ...customHeader,
    },
    onUploadProgress: (progressEvent) => {
      const percentCompleted = Math.round(
        (progressEvent.loaded * 100) / progressEvent.total
      );
      uploadProgress(percentCompleted);
    },
  });

  // Inject token into request
  instance.interceptors.request.use((config) => {
    if (!customHeader?.Authorization && access_token) {
      config.headers["Authorization"] = "Bearer " + access_token;
    }
    return config;
  });

  const refreshToken = async () => {
    if (!userId) {
      return Promise.reject("No refresh token");
    }

    try {
      const res = await axios.post(`${API_BASE_URL}/refresh-token`, {
        // refreshToken: refresh_token,
        userId,
      });

      if (res?.data?.status === 200) {
        const newAccessToken = res.data.accessToken;
        const newRefreshToken = res.data.refreshToken;

        localStorage.setItem("auth_token", newAccessToken);
        localStorage.setItem("refreshToken", newRefreshToken);

        access_token = newAccessToken;
        refresh_token = newRefreshToken;

        onTokenRefreshed(newAccessToken);

        return newAccessToken;
      } else {
        throw new Error("Session expired");
      }
    } catch (err) {
      sessionStorage.clear();
      localStorage.clear();
      const isAdmin = window.location.pathname.split("/")[1] === "admin";
      window.location.href =
        window.location.origin + isAdmin ? "/admin/signin" : "/login";
      return Promise.reject(err);
    }
  };

  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      if (error?.response?.data?.invalidToken && url !== "verify-token") {
        if (window.location?.pathname?.split("/").includes("admin")) {
          window.location.href = window.location.origin + "/admin/signin";
          return;
        } else {
          // window.location.href = window.location.origin + "/login";
          return;
        }
      }

      if (error?.response?.data?.tokenExpired && !originalRequest._retry) {
        originalRequest._retry = true;

        return new Promise((resolve, reject) => {
          queuedRequests.push({ resolve, reject, request: originalRequest });

          if (!isRefreshing) {
            isRefreshing = true;
            refreshToken()
              .then((newToken) => {
                queuedRequests.forEach(({ resolve, request }) => {
                  request.headers["Authorization"] = "Bearer " + newToken;
                  resolve(instance(request));
                });
                queuedRequests = [];
              })
              .catch((err) => {
                queuedRequests.forEach(({ reject }) => reject(err));
                queuedRequests = [];
              })
              .finally(() => {
                isRefreshing = false;
              });
          }
        });
      }

      return Promise.reject(error);
    }
  );

  // const requestPromise = instance[method.toLowerCase()](
  //   `${url}${
  //     payload.queries ? "?" + new URLSearchParams(payload.queries) : ""
  //   }`,
  //   method === "delete" && payload.body ? { data: payload.body } : payload.body
  // );

  const buildQueryString = (queries) => {
    const params = new URLSearchParams();
    for (const key in queries) {
      const value = queries[key];
      if (Array.isArray(value)) {
        value.forEach((v, i) => {
          params.append(`${key}[${i}]`, v);
        });
      } else {
        params.append(key, value);
      }
    }
    return params.toString();
  };

  const requestPromise = instance[method.toLowerCase()](
    `${url}${payload.queries ? "?" + buildQueryString(payload.queries) : ""}`,
    method === "delete" && payload.body ? { data: payload.body } : payload.body
  );

  inFlightRequests.set(requestKey, requestPromise);

  try {
    const res = await requestPromise;
    return res;
  } catch (error) {
    return error?.response;
  } finally {
    inFlightRequests.delete(requestKey);
  }
};

function onTokenRefreshed(newToken) {
  refreshSubscribers.forEach((cb) => cb(newToken));
  refreshSubscribers = [];
}
